import { CloseIcon, HamburgerIcon } from '@chakra-ui/icons'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Hide,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Stack,
  useBoolean,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'
import { signOut } from 'firebase/auth'
import { useRef } from 'react'
import { Link } from 'react-router-dom'
import useFirestoreUser from '../hooks/useFirestoreUser'
import useIsAdmin from '../hooks/useIsAdmin'
import useShowQuery from '../hooks/useHasAdvancedPermissions'
import useStore from '../store/useStore'
import { contactEmail } from '../utils/contactEmail'
import useImpersonationSession from '../hooks/useImpersonationSession'
import { stopImpersonation } from '../utils/impersonationService'
import { primaryServices } from '../utils/firebaseServiceContext'

const NavLink = ({
  text,
  to,
  onClose,
}: {
  text: string
  to: string
  onClose: () => void
}) => (
  <Box px={2} py={1}>
    <Link to={to} onClick={onClose}>
      <Button
        fontWeight='normal'
        color={useColorModeValue('black', 'white')}
        variant='link'
      >
        {text}
      </Button>
    </Link>
  </Box>
)

export default function Simple() {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const isAdmin = useIsAdmin()
  const showQuery = useShowQuery()
  const user = useFirestoreUser()

  // Check if user has admin role or medical diagnosis for Research access
  const hasAdminRole =
    user?.roles?.some((role) => role === 'admin' || role === 'superadmin') ||
    false

  const hasMedicalDiagnosis = user?.profile?.diagnosis === 'medical'

  const canAccessResearch = hasAdminRole || hasMedicalDiagnosis

  // Check if user data is still loading (undefined = loading, null or object = loaded)
  const isUserLoading = user === undefined

  // Check if user has completed their profile
  const hasCompletedProfile = user?.profile?.agreedToProfileCheckbox === true

  // Build menu links based on profile completion status
  // Return empty array while loading to prevent menu flash
  const Links = isUserLoading
    ? []
    : hasCompletedProfile
    ? [
        { text: 'Dashboard', to: '/' },
        { text: 'Profile', to: '/profile' },
        { text: 'Print Records', to: '/print' },
        { text: 'Help', to: '/help' },
        ...(canAccessResearch ? [{ text: 'Research', to: '/research' }] : []),
        ...(isAdmin ? [{ text: 'Admin', to: '/admin' }] : []),
        ...(showQuery ? [{ text: 'Query', to: '/query' }] : []),
      ]
    : [{ text: 'Profile', to: '/profile' }]

  return (
    <Hide breakpoint='print'>
      <Box bg={useColorModeValue('gray.100', 'gray.900')} px={4}>
        <Flex h={16} alignItems={'center'} justifyContent={'space-between'}>
          <IconButton
            size={'md'}
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            aria-label={'Open Menu'}
            colorScheme='gray'
            display={{ md: 'none' }}
            onClick={isOpen ? onClose : onOpen}
          />
          <HStack spacing={8} alignItems={'center'}>
            <Link to={hasCompletedProfile ? '/' : '/profile'}>
              <Image
                src='/lotus.jpeg'
                rounded='full'
                height={8}
                width={8}
                objectFit='cover'
                cursor='pointer'
              />
            </Link>
            <HStack
              as={'nav'}
              spacing={4}
              display={{ base: 'none', md: 'flex' }}
            >
              {Links.map((link) => (
                <NavLink key={link.to} {...link} onClose={onClose} />
              ))}
              {!isUserLoading && hasCompletedProfile && <Contact />}
            </HStack>
          </HStack>
          <Flex alignItems={'center'}>
            <Menu>
              <Initials />
              <MenuList>
                <Link to='/profile'>
                  <MenuItem>Edit Profile</MenuItem>
                </Link>
                {hasCompletedProfile && (
                  <Link to='/help'>
                    <MenuItem>Help</MenuItem>
                  </Link>
                )}
                <MenuDivider />
                <SignOut />
              </MenuList>
            </Menu>
          </Flex>
        </Flex>

        {isOpen ? (
          <Box pb={4} display={{ md: 'none' }}>
            <Stack as={'nav'} spacing={4}>
              {Links.map((link) => (
                <NavLink key={link.to} {...link} onClose={onClose} />
              ))}
              {!isUserLoading && hasCompletedProfile && <Contact />}
            </Stack>
          </Box>
        ) : null}
      </Box>
    </Hide>
  )
}

const Initials = () => {
  const user = useFirestoreUser()
  const email = useStore((state) => state.user?.email)
  return (
    <MenuButton
      as={Button}
      rounded='full'
      variant='link'
      cursor='pointer'
      minW={0}
    >
      <Avatar
        size='sm'
        name={
          user?.profile
            ? `${user.profile.firstName} ${user.profile.lastName}}`
            : email
            ? email.charAt(0).toUpperCase()
            : '?'
        }
      />
    </MenuButton>
  )
}

const SignOut = () => {
  const [isOpen, setIsOpen] = useBoolean()
  const cancelRef = useRef<any>()
  const session = useImpersonationSession()
  const actionLabel = session.isImpersonating ? 'Stop Impersonation' : 'Sign Out'

  return (
    <>
      <MenuItem onClick={setIsOpen.on}>{actionLabel}</MenuItem>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={setIsOpen.off}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              {actionLabel}
            </AlertDialogHeader>

            <AlertDialogBody>
              {session.isImpersonating
                ? 'Stop acting as this patient and return to your administrator session?'
                : 'Are you sure you want to sign out?'}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                colorScheme='gray'
                ref={cancelRef}
                onClick={setIsOpen.off}
              >
                Cancel
              </Button>
              <Button
                colorScheme='red'
                onClick={() => {
                  setIsOpen.off()
                  const action = session.isImpersonating
                    ? stopImpersonation('explicit')
                    : signOut(primaryServices.auth).then(() => {
                        window.location.href = '/'
                      })
                  void action
                }}
                ml={3}
              >
                {actionLabel}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}

const Contact = () => {
  const { isOpen, onClose, onOpen } = useDisclosure()
  const leastDestructiveRef = useRef<any>()
  return (
    <Box px={2} py={1}>
      <Button
        fontWeight='normal'
        color={useColorModeValue('black', 'white')}
        variant='link'
        onClick={onOpen}
      >
        Contact
      </Button>
      <AlertDialog
        isOpen={isOpen}
        onClose={onClose}
        leastDestructiveRef={leastDestructiveRef}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Contact</AlertDialogHeader>
            <AlertDialogCloseButton />
            <AlertDialogBody>
              To contact us, please email us at:{' '}
              <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={leastDestructiveRef} onClick={onClose}>
                OK
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}
